import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Carregando, Estado } from '@/components/Estado';
import { IconeBusca } from '@/components/Icones';
import { melhorAtributoDeRotulo, useApresentacao } from '@/features/feicao/apresentacao';
import { useSelecao } from '@/features/feicao/selecao';
import { useExploracao } from '@/features/exploracao/contexto';
import { useI18n } from '@/i18n/I18nProvider';
import { useCamadas } from '@/map/camadas';
import { useMapa } from '@/map/Mapa';
import { listarFeicoes } from '@/services/geo-engine/requests';
import { TIPO_CSV, baixarArquivo, construirCsv, nomeDoCsvDeFeicoes, } from '@/utils/exportacao';
const LIMITE_DA_PAGINA = 20;
const ATRASO_DA_BUSCA_MS = 300;
const MENSAGEM_DE_RECORTE = {
    mapa: 'camada.erroMapaIndisponivel',
    coordenadas: 'camada.erroCoordenadasInvalidas',
    limites: 'camada.erroLimitesGeograficos',
    extensao: 'camada.erroExtensaoInvalida',
};
/**
 * Exploração de entidades de uma camada: paginação manual, busca textual simples pelo atributo
 * de rótulo humano, checkbox "Todos" e seleção individual. Não baixa geometrias e não mantém
 * uma representação paralela: o filtro por id vai direto para as layers MVT existentes.
 */
export function SeletorDeFeicoes({ camada }) {
    const { obterSelecaoDaCamada, definirSelecaoDaCamada } = useCamadas();
    const { selecionar } = useSelecao();
    const { irParaInspecao } = useExploracao();
    const { t, traduzirErro } = useI18n();
    const { rotulo: rotuloDaFeicao } = useApresentacao();
    const selecao = obterSelecaoDaCamada(camada.id);
    const todos = selecao.todos;
    const idsMarcados = new Set(selecao.ids);
    const atributoPreferido = melhorAtributoDeRotulo(camada.atributos_mvt);
    const buscaDisponivel = atributoPreferido !== null;
    const [termo, setTermo] = useState('');
    const termoEstabilizado = useDebounced(termo, ATRASO_DA_BUSCA_MS);
    const [pagina, setPagina] = useState(null);
    const [cursor, setCursor] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [erroPaginacao, setErroPaginacao] = useState(null);
    // Recorte espacial: snapshot da extensão visível no momento da aplicação. Fica atrelado à camada
    // em que foi aplicado (outra camada nunca o reutiliza) e a identidade do objeto muda a cada
    // aplicação, de modo que reaplicar o mesmo recorte também reinicia a consulta.
    const mapa = useMapa();
    const [recorte, setRecorte] = useState(null);
    const [erroDeRecorte, setErroDeRecorte] = useState(null);
    const recorteAtual = recorte !== null && recorte.camadaId === camada.id ? recorte : null;
    const bboxAplicada = recorteAtual?.bbox ?? null;
    // Camada cujo recorte acabou de ser removido: habilita a confirmação após a consulta.
    const [removidoEm, setRemovidoEm] = useState(null);
    const consultaEmAndamento = carregando;
    const consultaConcluida = pagina !== null && !carregando && erro === null;
    const camadaIdRef = useRef(camada.id);
    camadaIdRef.current = camada.id;
    // Refletem o estado atual sem entrar em deps para evitar refetch a cada render.
    const atributoRef = useRef(atributoPreferido);
    atributoRef.current = atributoPreferido;
    // Uma única fonte de cancelamento por carregamento.
    const controladorRef = useRef(null);
    // Camada troca: zera resultados e recarrega a primeira página uma vez.
    useEffect(() => {
        setPagina(null);
        setCursor(null);
        setErro(null);
        setErroPaginacao(null);
        setCarregando(true);
        controladorRef.current?.abort();
        const ctrl = new AbortController();
        controladorRef.current = ctrl;
        const filtros = termoEstabilizado.trim() !== '' && atributoRef.current !== null
            ? [`${atributoRef.current}:${termoEstabilizado.trim()}`]
            : undefined;
        void listarFeicoes(camadaIdRef.current, { limit: LIMITE_DA_PAGINA, filtros, bbox: bboxAplicada ?? undefined }, ctrl.signal).then((resposta) => {
            if (ctrl.signal.aborted) {
                return;
            }
            setCarregando(false);
            if (resposta === null) {
                return;
            }
            setPagina(resposta.dados.itens);
            setCursor(resposta.dados.proximo_cursor);
        }, (falha) => {
            if (ctrl.signal.aborted) {
                return;
            }
            setCarregando(false);
            setErro({ falha: falha instanceof Error ? falha : null });
        });
        return () => {
            ctrl.abort();
        };
    }, [camada.id, termoEstabilizado, recorteAtual, bboxAplicada]);
    const carregarMais = () => {
        if (carregando || cursor === null) {
            return;
        }
        setErroPaginacao(null);
        setCarregando(true);
        controladorRef.current?.abort();
        const ctrl = new AbortController();
        controladorRef.current = ctrl;
        const filtros = termoEstabilizado.trim() !== '' && atributoRef.current !== null
            ? [`${atributoRef.current}:${termoEstabilizado.trim()}`]
            : undefined;
        void listarFeicoes(camadaIdRef.current, { limit: LIMITE_DA_PAGINA, cursor, filtros, bbox: bboxAplicada ?? undefined }, ctrl.signal).then((resposta) => {
            if (ctrl.signal.aborted) {
                return;
            }
            setCarregando(false);
            if (resposta === null) {
                return;
            }
            setPagina((atual) => [...(atual ?? []), ...resposta.dados.itens]);
            setCursor(resposta.dados.proximo_cursor);
        }, (falha) => {
            if (ctrl.signal.aborted) {
                return;
            }
            setCarregando(false);
            setErroPaginacao({ falha: falha instanceof Error ? falha : null });
        });
    };
    const aplicarRecorte = () => {
        if (mapa === null) {
            setErroDeRecorte('mapa');
            return;
        }
        const limites = mapa.getBounds();
        const oeste = limites.getWest();
        const sul = limites.getSouth();
        const leste = limites.getEast();
        const norte = limites.getNorth();
        if (![oeste, sul, leste, norte].every(Number.isFinite)) {
            setErroDeRecorte('coordenadas');
            return;
        }
        if (oeste < -180 || leste > 180 || sul < -90 || norte > 90) {
            setErroDeRecorte('limites');
            return;
        }
        if (oeste >= leste || sul >= norte) {
            setErroDeRecorte('extensao');
            return;
        }
        setErroDeRecorte(null);
        setRemovidoEm(null);
        setRecorte({ camadaId: camada.id, bbox: `${oeste},${sul},${leste},${norte}` });
    };
    const removerRecorte = () => {
        setErroDeRecorte(null);
        setRemovidoEm(camada.id);
        setRecorte(null);
    };
    // O CSV representa a lista de consulta já carregada. Enquanto uma consulta está em andamento (ou
    // a busca digitada ainda não virou consulta), `pagina` pertence à consulta anterior e não é exportada.
    const consultaDefasada = carregando || termo !== termoEstabilizado;
    const exportarCsv = () => {
        if (pagina === null || pagina.length === 0 || consultaDefasada) {
            return;
        }
        baixarArquivo(construirCsv(pagina), nomeDoCsvDeFeicoes(camada.id, pagina.length), TIPO_CSV);
    };
    const alternarMarcada = (id) => {
        if (todos) {
            definirSelecaoDaCamada(camada.id, { todos: false, ids: [id] });
        }
        else {
            const proximos = idsMarcados.has(id)
                ? selecao.ids.filter((outro) => outro !== id)
                : [...selecao.ids, id];
            definirSelecaoDaCamada(camada.id, { todos: false, ids: proximos });
        }
    };
    const alternarTodos = () => {
        if (todos) {
            definirSelecaoDaCamada(camada.id, { todos: false, ids: [] });
        }
        else {
            definirSelecaoDaCamada(camada.id, { todos: true, ids: [] });
        }
    };
    return (_jsxs("div", { className: "seletor-de-feicoes", children: [_jsx("div", { className: "seletor-de-feicoes__topo", children: _jsxs("label", { className: "camadas__alternador", children: [_jsx("input", { type: "checkbox", checked: todos, onChange: alternarTodos }), _jsx("span", { children: t('camada.todos') })] }) }), _jsxs("section", { className: "recorte-espacial", "aria-labelledby": "recorte-espacial-titulo", children: [_jsx("h4", { className: "recorte-espacial__titulo", id: "recorte-espacial-titulo", children: t('camada.recorteTitulo') }), _jsx("p", { className: "recorte-espacial__texto", children: t('camada.recorteTexto') }), _jsxs("div", { className: "recorte-espacial__acoes", children: [_jsx("button", { type: "button", className: "botao recorte-espacial__botao recorte-espacial__botao--primario", onClick: aplicarRecorte, disabled: mapa === null || consultaEmAndamento, children: t('camada.recorteFiltrar') }), bboxAplicada !== null ? (_jsx("button", { type: "button", className: "botao recorte-espacial__botao recorte-espacial__botao--secundario", onClick: removerRecorte, disabled: consultaEmAndamento, children: t('camada.recorteRemover') })) : null] }), bboxAplicada !== null ? (_jsxs("div", { className: "recorte-espacial__ativo", role: "status", children: [_jsx("strong", { children: t('camada.recorteAtivo') }), _jsx("span", { className: "recorte-espacial__nota", children: t('camada.recorteNota') }), consultaConcluida && pagina !== null ? (_jsx("span", { className: "recorte-espacial__resultado", children: cursor !== null
                                    ? t('camada.recorteResultadoComMais', { quantidade: pagina.length })
                                    : t('camada.recorteResultado', { quantidade: pagina.length }) })) : null] })) : removidoEm === camada.id && consultaConcluida ? (_jsx("p", { className: "recorte-espacial__nota", role: "status", children: t('camada.recorteRemovido') })) : null, erroDeRecorte !== null ? (_jsx("p", { className: "recorte-espacial__erro", role: "alert", children: t(MENSAGEM_DE_RECORTE[erroDeRecorte]) })) : null] }), buscaDisponivel ? (_jsxs("label", { className: "busca", children: [_jsx("span", { className: "busca__icone", "aria-hidden": "true", children: _jsx(IconeBusca, {}) }), _jsx("span", { className: "campo__rotulo sr-apenas", children: t('camada.buscarPorNome') }), _jsx("input", { className: "campo__entrada", type: "search", value: termo, onChange: (evento) => setTermo(evento.target.value), placeholder: t('camada.buscarPlaceholder') })] })) : (_jsx("p", { className: "nota", children: t('camada.buscaIndisponivel') })), erro !== null ? (_jsx(Estado, { titulo: t('camada.erroTitulo'), texto: erro.falha !== null ? traduzirErro(erro.falha) : t('camada.falhaConsultar'), variante: "erro" })) : null, carregando && pagina === null ? _jsx(Carregando, { texto: t('camada.consultandoFeicoes') }) : null, pagina !== null && pagina.length === 0 && !carregando && erro === null ? (_jsx("p", { className: "nota", children: bboxAplicada !== null
                    ? t('camada.nenhumaNaArea')
                    : t('camada.nenhumaEncontrada') })) : null, pagina !== null && pagina.length > 0 ? (_jsxs("div", { className: "exportacao-csv", children: [_jsx("button", { type: "button", className: "botao exportacao-csv__botao", onClick: exportarCsv, disabled: consultaDefasada, children: t('exportacao.csv', { quantidade: pagina.length }) }), _jsx("span", { className: "exportacao-csv__nota", children: t('exportacao.csvNota') })] })) : null, pagina !== null && pagina.length > 0 ? (_jsx("ul", { className: "feicoes", "aria-label": t('camada.feicoes'), children: pagina.map((feicao) => {
                    const marcada = !todos && idsMarcados.has(feicao.id);
                    return (_jsxs("li", { className: "feicoes__item feicoes__item--selecionavel", "data-selecionado": marcada ? 'sim' : undefined, children: [_jsx("label", { className: "feicoes__checkbox", children: _jsx("input", { type: "checkbox", checked: marcada, disabled: todos, onChange: () => alternarMarcada(feicao.id), "aria-label": t('camada.selecionarFeicao', {
                                        nome: rotuloDaFeicao(feicao.atributos, feicao.id),
                                    }) }) }), _jsxs("div", { className: "feicoes__info", children: [_jsx("span", { className: "feicoes__titulo", title: rotuloDaFeicao(feicao.atributos, feicao.id), children: rotuloDaFeicao(feicao.atributos, feicao.id) }), _jsxs("span", { className: "feicoes__detalhe", children: ["#", feicao.id] })] }), _jsx("button", { type: "button", className: "botao botao--discreto botao--texto feicoes__inspecionar", "aria-label": t('camada.inspecionarFeicao', {
                                    nome: rotuloDaFeicao(feicao.atributos, feicao.id),
                                }), onClick: () => {
                                    // Abre o inspetor primeiro para que `NivelAtual` já
                                    // monte `<DetalhesDaFeicao>` ao chegar a geometria.
                                    irParaInspecao(camada.id);
                                    void selecionar(camada.id, feicao.id, { enquadrar: true });
                                }, children: t('camada.inspecionar') })] }, feicao.id));
                }) })) : null, erroPaginacao !== null ? (_jsx(Estado, { titulo: t('camada.falhaCarregarMaisTitulo'), texto: erroPaginacao.falha !== null
                    ? traduzirErro(erroPaginacao.falha)
                    : t('camada.falhaCarregarMais'), variante: "erro" })) : null, cursor !== null && pagina !== null ? (_jsx("button", { type: "button", className: "botao", onClick: carregarMais, disabled: carregando, children: carregando ? t('global.carregando') : t('camada.carregarMais') })) : null] }));
}
function useDebounced(valor, atrasoMs) {
    const [atrasado, setAtrasado] = useState(valor);
    useEffect(() => {
        const id = setTimeout(() => setAtrasado(valor), atrasoMs);
        return () => clearTimeout(id);
    }, [valor, atrasoMs]);
    return atrasado;
}
